import { useEffect, useRef } from "react";

/**
 * InteractiveBackdrop - the bespoke "mapping the competitive landscape" motif.
 *
 * This deliberately AVOIDS the generic cursor-reactive particle/constellation
 * field (a well-known "AI-generated / vibe-coded" tell) in favour of something
 * tied to the product story: a slow intelligence RADAR sweep crossing a faint
 * TOPOGRAPHIC-CONTOUR field. It reads as "scanning the terrain for signals"
 * rather than a decorative dot-web.
 *
 * Three layers, all low-opacity, behind ALL content:
 *   1. CSS aurora-mesh gradient (.ns-mesh) - deep navy base with cyan + brand
 *      glows (NOT rainbow). Owned by index.css; neutralised under reduced-motion.
 *   2. A canvas drawing:
 *        - concentric range rings + faint radial bearings (the radar grid),
 *        - drifting topographic contour bands (intelligence "terrain"),
 *        - a single slow sweep wedge with a trailing gradient, anchored at the
 *          top-right so it never sits behind the primary reading column.
 *   3. A film-grain / noise overlay (tiled, cheap) so the gradient never looks
 *      like a flat default fill - the texture that makes it feel bespoke.
 *
 * PERFORMANCE & ACCESSIBILITY (hard requirements):
 *   - Single requestAnimationFrame loop, DPR capped (<= 1.5) for fill-rate.
 *   - Pauses when the tab is hidden (visibilitychange) AND when the backdrop is
 *     scrolled out of view (IntersectionObserver) - zero work off-screen.
 *   - Throttled to ~30fps so it never competes with the live streaming view.
 *   - The grain tile + contour geometry are precomputed once per resize, so the
 *     per-frame cost is a handful of arcs + one wedge fill (very light).
 *   - Under prefers-reduced-motion: reduce -> NO canvas loop at all; a single
 *     static frame (rings + contours + grain, no sweep) is painted once. The
 *     media query is re-checked live.
 *   - pointer-events: none everywhere, so it never intercepts clicks.
 *
 * Designed to sit as the first child of a `position: relative` container with
 * the page content layered above (z-index > 0).
 */

const CYAN = "34,211,238"; // neon cyan rgb
const BRAND = "74,86,232"; // brand-600 rgb

export function InteractiveBackdrop({ className = "" }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvasEl = canvasRef.current;
    const wrapEl = wrapRef.current;
    if (!canvasEl || !wrapEl) return;
    const ctx2d = canvasEl.getContext("2d");
    if (!ctx2d) return;
    const canvas: HTMLCanvasElement = canvasEl;
    const wrap: HTMLDivElement = wrapEl;
    const ctx: CanvasRenderingContext2D = ctx2d;

    const reduceMq = window.matchMedia("(prefers-reduced-motion: reduce)");
    let reduceMotion = reduceMq.matches;

    let dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let width = 0;
    let height = 0;
    let rafId: number | null = null;
    let running = false;
    let visibleInViewport = true;

    // Radar origin: top-right corner, slightly off-canvas, so the sweep crosses
    // open space and never washes out the primary left-aligned reading column.
    let originX = 0;
    let originY = 0;
    let maxR = 0;
    let angle = -Math.PI / 2; // current sweep bearing
    let lastTs = 0;

    // Topographic contour field - a set of slow-drifting blob ring sets,
    // precomputed per resize so the per-frame draw stays cheap.
    interface Contour {
      cx: number;
      cy: number;
      base: number; // base radius
      rings: number; // number of nested rings
      gap: number; // spacing between rings
      drift: number; // px/sec horizontal drift
      phase: number;
    }
    let contours: Contour[] = [];

    // Precomputed grain pattern (drawn once, reused every frame).
    let grainPattern: CanvasPattern | null = null;

    function buildContours() {
      const count = width < 640 ? 2 : width < 1024 ? 3 : 4;
      contours = Array.from({ length: count }, (_, i) => {
        const cx = (width / (count + 1)) * (i + 1) + (Math.random() - 0.5) * width * 0.08;
        const cy = height * (0.35 + Math.random() * 0.45);
        const base = Math.min(width, height) * (0.06 + Math.random() * 0.05);
        return {
          cx,
          cy,
          base,
          rings: 5 + Math.floor(Math.random() * 3),
          gap: base * (0.7 + Math.random() * 0.4),
          drift: (Math.random() - 0.5) * 6,
          phase: Math.random() * Math.PI * 2,
        };
      });
    }

    function buildGrain() {
      const size = 96;
      const g = document.createElement("canvas");
      g.width = size;
      g.height = size;
      const gc = g.getContext("2d");
      if (!gc) {
        grainPattern = null;
        return;
      }
      const img = gc.createImageData(size, size);
      const data = img.data;
      for (let i = 0; i < data.length; i += 4) {
        // Monochrome speckle; very low alpha so it only "tooths" the gradient.
        const v = Math.random() * 255;
        data[i] = v;
        data[i + 1] = v;
        data[i + 2] = v;
        data[i + 3] = Math.random() * 14; // 0..14 alpha
      }
      gc.putImageData(img, 0, 0);
      grainPattern = ctx.createPattern(g, "repeat");
    }

    function resize() {
      // Canvas is viewport-fixed (styled position:fixed) so size it to the
      // viewport, not the (possibly very tall) scrolling parent. This bounds the
      // fill-rate regardless of page length - critical for the live stream view.
      width = Math.max(1, Math.floor(window.innerWidth));
      height = Math.max(1, Math.floor(window.innerHeight));
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      originX = width + width * 0.04;
      originY = -height * 0.06;
      maxR = Math.hypot(width * 1.08, height * 1.12);

      buildContours();
      buildGrain();
    }

    function drawContours(tSec: number) {
      ctx.lineWidth = 1;
      for (const c of contours) {
        const cx = c.cx + Math.sin(tSec * 0.06 + c.phase) * c.drift * 4;
        const cy = c.cy + Math.cos(tSec * 0.05 + c.phase) * c.drift * 2;
        for (let r = 0; r < c.rings; r++) {
          const radius = c.base + r * c.gap + Math.sin(tSec * 0.12 + r + c.phase) * (c.gap * 0.12);
          // Inner rings brighter, outer rings fade out.
          const alpha = 0.05 * (1 - r / c.rings);
          if (alpha <= 0.003) continue;
          ctx.strokeStyle = `rgba(${CYAN},${alpha.toFixed(3)})`;
          ctx.beginPath();
          ctx.ellipse(cx, cy, radius, radius * 0.82, 0, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
    }

    function drawRadarGrid() {
      // Concentric range rings around the off-canvas top-right origin.
      ctx.strokeStyle = `rgba(${CYAN},0.05)`;
      ctx.lineWidth = 1;
      const ringStep = maxR / 7;
      for (let i = 1; i <= 7; i++) {
        ctx.beginPath();
        ctx.arc(originX, originY, ringStep * i, 0, Math.PI * 2);
        ctx.stroke();
      }
      // Faint radial bearings.
      ctx.strokeStyle = `rgba(${BRAND},0.05)`;
      const bearings = 6;
      for (let i = 0; i < bearings; i++) {
        const a = Math.PI / 2 + (Math.PI / 2) * (i / (bearings - 1)); // lower-left quadrant fan
        ctx.beginPath();
        ctx.moveTo(originX, originY);
        ctx.lineTo(originX + Math.cos(a) * maxR, originY + Math.sin(a) * maxR);
        ctx.stroke();
      }
    }

    function drawSweep() {
      // A single wedge with a trailing gradient that fades behind the leading
      // edge - reads as a slow scanning beam, not a spinning radar gimmick.
      const trail = 0.85; // radians of trail

      ctx.save();
      ctx.translate(originX, originY);

      // Trailing fan, built from thin wedges fading toward the tail.
      const steps = 26;
      for (let i = 0; i < steps; i++) {
        const t = i / steps;
        const a0 = angle - t * trail;
        const a1 = angle - (t + 1 / steps) * trail;
        const alpha = 0.1 * (1 - t) * (1 - t);
        if (alpha <= 0.002) continue;
        ctx.fillStyle = `rgba(${CYAN},${alpha.toFixed(3)})`;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.arc(0, 0, maxR, a0, a1, true);
        ctx.closePath();
        ctx.fill();
      }

      // Leading edge line.
      ctx.strokeStyle = `rgba(${CYAN},0.22)`;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(Math.cos(angle) * maxR, Math.sin(angle) * maxR);
      ctx.stroke();

      ctx.restore();
    }

    function drawGrain() {
      if (!grainPattern) return;
      ctx.save();
      ctx.fillStyle = grainPattern;
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
    }

    function render(tSec: number) {
      ctx.clearRect(0, 0, width, height);
      drawRadarGrid();
      drawContours(tSec);
      drawSweep();
      drawGrain();
    }

    function frame(ts: number) {
      if (!running) return;
      // Throttle to ~30fps so the backdrop never starves the streaming view.
      if (ts - lastTs < 33) {
        rafId = requestAnimationFrame(frame);
        return;
      }
      const dt = lastTs ? (ts - lastTs) / 1000 : 0;
      lastTs = ts;

      // Slow sweep: a full revolution every ~16s.
      angle += dt * (Math.PI * 2) / 16;
      if (angle > Math.PI * 3) angle -= Math.PI * 2;

      render(ts / 1000);
      rafId = requestAnimationFrame(frame);
    }

    function paintStaticFrame() {
      // One static composition for reduced-motion (no sweep beam, no animation).
      ctx.clearRect(0, 0, width, height);
      drawRadarGrid();
      drawContours(0);
      drawGrain();
    }

    function start() {
      if (reduceMotion) {
        paintStaticFrame();
        return;
      }
      if (running) return;
      if (document.hidden || !visibleInViewport) return;
      running = true;
      lastTs = 0;
      rafId = requestAnimationFrame(frame);
    }

    function stop() {
      running = false;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    function onVisibility() {
      if (document.hidden) stop();
      else start();
    }

    function onReduceChange() {
      reduceMotion = reduceMq.matches;
      if (reduceMotion) {
        stop();
        paintStaticFrame();
      } else {
        start();
      }
    }

    // IntersectionObserver: when the whole backdrop surface scrolls out of view,
    // the loop pauses (zero work off-screen).
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        visibleInViewport = entry?.isIntersecting ?? true;
        if (visibleInViewport) start();
        else stop();
      },
      { threshold: 0 },
    );
    io.observe(wrap);

    function onResize() {
      resize();
      if (reduceMotion) paintStaticFrame();
    }

    resize();
    start();

    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);
    if (reduceMq.addEventListener) reduceMq.addEventListener("change", onReduceChange);
    else reduceMq.addListener(onReduceChange);

    return () => {
      stop();
      io.disconnect();
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
      if (reduceMq.removeEventListener) reduceMq.removeEventListener("change", onReduceChange);
      else reduceMq.removeListener(onReduceChange);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      {/* Layers are viewport-fixed so the field stays put while content scrolls
          and the canvas fill-rate is bounded to the viewport regardless of page
          length (critical for the long live-streaming view). */}
      {/* Layer 1 - flowing aurora gradient mesh (CSS; reduced-motion neutralised
          globally by the @media rule in index.css that zeroes animations). */}
      <div className="ns-mesh fixed inset-0" />
      {/* Deep vignette keeps AA text contrast over the mesh + radar. */}
      <div
        className="fixed inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(2,6,23,0) 0%, rgba(2,6,23,0.55) 70%, rgba(2,6,23,0.86) 100%)",
        }}
      />
      {/* Layer 2 + 3 - radar sweep + topographic contours + film grain (the
          canvas paints a single static frame under reduced motion). */}
      <canvas ref={canvasRef} className="fixed inset-0 opacity-80" />
    </div>
  );
}
